import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { authApi, useAuthStore } from '@/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/toaster';
import { ApiEnvSwitcher } from '@/components/ApiEnvSwitcher';
import { DebugLoginPanel } from '@/components/DebugLoginPanel';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { config } from '@/lib/config';
import { LANGUAGES, resolveLanguageCode } from '@/i18n/languages';

type LocationState = { from?: string } | undefined;

export default function Login() {
  const { t, i18n } = useTranslation();

  // Two-language toggle, mirroring v1's login footer link. Resolves the
  // current language (which may be `en-US` / `fr-CA`) to one of the two
  // short codes the app supports, then flips to the other on click. We
  // also strip `?lang=` from the URL so a re-load doesn't undo the
  // user's choice — v1 does the same.
  const currentLang = resolveLanguageCode(i18n.language);
  const otherLang = LANGUAGES.find((l) => l.code !== currentLang) ?? LANGUAGES[0];
  const toggleLanguage = () => {
    i18n.changeLanguage(otherLang.code);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('lang');
      window.history.replaceState({}, '', url);
    } catch {
      // SSR or unparseable URL — language change still applied.
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const setToken = useAuthStore((s) => s.setToken);
  const setClinician = useAuthStore((s) => s.setClinician);
  const setPendingCredentials = useAuthStore((s) => s.setPendingCredentials);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkPending, setMagicLinkPending] = useState(false);

  // Magic-link landing: if `/login?secureToken=…`, exchange the token for a bearer
  // and skip the credential form entirely. Mirrors the v1 flow in login.tsx:118.
  const magicLinkAttempted = useRef(false);
  useEffect(() => {
    if (magicLinkAttempted.current) return;
    const secureToken = searchParams.get('secureToken') ?? searchParams.get('secure_token');
    if (!secureToken) return;
    magicLinkAttempted.current = true;
    setMagicLinkPending(true);
    (async () => {
      try {
        const response = await authApi.authorizeMagicLink(secureToken);
        const accessToken = response.oauth_token?.access_token;
        if (!accessToken) {
          // Drop the param so the page becomes a normal login form on the next render.
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('secureToken');
            next.delete('secure_token');
            return next;
          }, { replace: true });
          setError(t('That link is invalid or has expired.'));
          return;
        }
        setToken(accessToken);
        if (response.clinician) setClinician(response.clinician);
        navigate('/patients', { replace: true });
      } catch {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('secureToken');
          next.delete('secure_token');
          return next;
        }, { replace: true });
        setError(t('That link is invalid or has expired.'));
      } finally {
        setMagicLinkPending(false);
      }
    })();
  }, [searchParams, setSearchParams, setToken, setClinician, navigate, t]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await authApi.authorize({ username, password });

      if (response.reason === 'mfa_token_required') {
        setPendingCredentials({ username, password });
        navigate('/login/2fa');
        return;
      }

      const accessToken = response.oauth_token?.access_token;
      if (!accessToken) {
        setError(t('Unable to authenticate'));
        return;
      }
      setToken(accessToken);
      if (response.clinician) setClinician(response.clinician as Record<string, unknown>);
      track(ANALYTICS_EVENTS.LOGIN);
      const dest = (location.state as LocationState)?.from ?? '/patients';
      navigate(dest, { replace: true });
    } catch (err) {
      const axiosErr = err as AxiosError<{ reason?: string; success?: boolean }>;
      if (axiosErr.response?.status === 401 && axiosErr.response.data?.reason === 'mfa_token_required') {
        setPendingCredentials({ username, password });
        navigate('/login/2fa');
        return;
      }
      setError(t('Invalid email or password'));
      toast({ title: t('Sign in failed'), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (magicLinkPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>{t('Signing you in…')}</CardTitle>
            <CardDescription>{t('Verifying your sign-in link.')}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('Sign in to RxFood')}</CardTitle>
          <CardDescription>{t('Use your clinician credentials.')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1">
              <Label htmlFor="username">{t('Email')}</Label>
              <Input
                id="username"
                type="email"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="password">{t('Password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? t('Signing in…') : t('Sign in')}
            </Button>
            <div className="flex justify-between text-xs">
              <Link to="/forgot-password" className="text-primary hover:underline">
                {t('Forgot password?')}
              </Link>
              <Link to="/signup" className="text-primary hover:underline">
                {t('Sign up')}
              </Link>
            </div>
          </form>
          {/* Dev-server-only tooling: the API endpoint switcher and the
              local debug-login bypass. Excluded from every built artifact so
              production is pinned to https://api.rxfoodapp.com with no API
              opt-out on the login screen. */}
          {import.meta.env.DEV && (
            <div className="mt-4 space-y-3">
              <ApiEnvSwitcher />
              <DebugLoginPanel />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Below-the-card footer — mirrors v1's login screen
          (clinician/src/pages/login.tsx:216-247): the legal copy,
          a build-stamp for triage, and a one-click language toggle.
          Lives outside the Card so it doesn't crowd the form fields. */}
      <div className="flex w-full max-w-sm flex-col items-center gap-2 text-center text-xs text-muted-foreground">
        <p>
          {t('By logging in you agree to RxFood’s')}{' '}
          <a
            href="https://rxfood.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {t('Privacy policy')}
          </a>{' '}
          {t('and')}{' '}
          <a
            href="https://rxfood.com/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {t('terms & conditions')}
          </a>
        </p>
        <p className="tabular-nums text-[0.625rem] opacity-70">{config.RELEASE}</p>
        <button
          type="button"
          onClick={toggleLanguage}
          className="text-primary hover:underline"
        >
          {otherLang.label}
        </button>
      </div>
    </div>
  );
}
